import type { ValidationErrors } from 'final-form';
import Cookies from 'js-cookie';
import {
  type ConfirmPurchaseRequest,
  encryptLifeForm,
} from '../api/calculator';
import {
  BeneficiaryType,
  type NamedCompanyOrCommunityBeneficiary,
  type NamedPersonBeneficiary,
} from '../dto/beneficiary';
import {
  type CoverageProduct,
  MarketableProductName,
} from '../dto/marketableProduct';
import i18n from '../i18n';
import type { BackgroundInfoForm } from '../individual/types/backgroundInfoForm';
import type {
  IndividualLifeForm,
  SelectedCoverage,
  UnAuthenticatedInitialState,
} from '../individual/types/initialState';
import { TestGroup } from '../types';
import { Feature } from '../types/featureSwitches';
import { getInitialState, isFeatureEnabled, redirectTo } from '../utils';
import { required } from '../utils/validators';
import { type SelectedSumInsuredPremiums } from 'individual/types/selectedSumInsuredPremiums';

export function getEmptyBackground(): BackgroundInfoForm {
  return {
    dateOfBirth: '',
    monthlyIncome: undefined,
    debt: undefined,
    children: 0,
    smokingStatus: null,
    workingStatus: null,
  };
}

export const redirectToAuth = async (
  url: string,
  formValues: IndividualLifeForm
) => {
  const response = await encryptLifeForm(formValues);
  redirectTo(url, {
    user_input: response.encrypted,
    product:
      getInitialState()?.marketableProduct?.name ??
      MarketableProductName.INDIVIDUAL_LIFE,
  });
};

export const validateNamedBeneficiariesTotalPercentage = (
  selectedBeneficiary: BeneficiaryType,
  namedBeneficiariesTotalPercentage: number | undefined
): string | undefined => {
  return selectedBeneficiary === BeneficiaryType.NAMED_BENEFICIARY &&
    namedBeneficiariesTotalPercentage !== 100
    ? i18n.t('beneficiarySelect.totalPercentageError2')
    : undefined;
};

export const validatePurchaseRequest = (
  request: ConfirmPurchaseRequest,
  namedBeneficiariesTotalPercentage: number | undefined
): ValidationErrors => {
  const _errors = {
    marketableProductName: required(
      request.calculatorInput.marketableProductName
    ),
    backgroundInfo: required(request.calculatorInput.backgroundInfo),
    coverages: required(request.calculatorInput.coverages),
    beneficiaryType: required(request.beneficiaryType),
    namedBeneficiaries: validateNamedBeneficiariesTotalPercentage(
      request.beneficiaryType,
      namedBeneficiariesTotalPercentage
    ),
    installmentAmount: required(request.calculatorInput.installmentAmount),
    consentForDocuments: required(request.consentForDocuments),
  };
  if (Object.values(_errors).filter((x) => x !== undefined).length > 0)
    return _errors;
};

export const unauthenticatedSubmit = async (
  backgroundInfoForm: BackgroundInfoForm,
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums,
  selectedCoverages: CoverageProduct[],
  installmentAmount: number
) => {
  const coverages: Partial<Record<CoverageProduct, SelectedCoverage>> = {};

  selectedCoverages.forEach((coverageProduct) => {
    const sumInsuredDetails = selectedSumInsuredPremiums[coverageProduct];
    if (sumInsuredDetails) {
      coverages[coverageProduct] = {
        sumInsured: sumInsuredDetails.sumInsured,
        deductibleDays: sumInsuredDetails.deductibleDays ?? null,
      };
    }
  });

  const formData: IndividualLifeForm = {
    marketableProductName: (getInitialState() as UnAuthenticatedInitialState)
      .marketableProduct.name,
    backgroundInfo: backgroundInfoForm,
    coverages,
    installmentAmount,
  };

  await redirectToAuth(
    window.__RHVKLSO002_CONFIG.LIFEPURCHASE_AUTH_REDIRECT_URL,
    formData
  ).catch((e) => {
    throw e;
  });
};

export const createConfirmPurchaseRequest = ({
  backgroundInfoForm,
  selectedSumInsuredPremiums,
  selectedBeneficiary,
  maritalRightsExclusion,
  namedBeneficiaries,
  numberOfInstallments,
  consentForDocuments,
  languageConfirmed,
  marketableProductName,
  selectedCoverages,
}: {
  backgroundInfoForm: BackgroundInfoForm;
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums;
  selectedBeneficiary: BeneficiaryType;
  maritalRightsExclusion: boolean;
  namedBeneficiaries: Array<
    NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  >;
  numberOfInstallments: number;
  consentForDocuments: boolean;
  languageConfirmed: boolean | undefined;
  marketableProductName: string;
  selectedCoverages: CoverageProduct[];
}): ConfirmPurchaseRequest => {
  const sumInsureds = selectedCoverages.reduce<
    Partial<Record<CoverageProduct, SelectedCoverage>>
  >(
    (sums, coverageProduct) => ({
      ...sums,
      [coverageProduct]: {
        sumInsured: selectedSumInsuredPremiums[coverageProduct]?.sumInsured,
        deductibleDays:
          selectedSumInsuredPremiums[coverageProduct]?.deductibleDays ?? null,
      },
    }),
    {}
  );

  return {
    calculatorInput: {
      marketableProductName,
      backgroundInfo: backgroundInfoForm,
      coverages: sumInsureds,
      installmentAmount: numberOfInstallments,
    },
    beneficiaryType: selectedBeneficiary,
    beneficiaryMaritalRightsExcluded:
      selectedBeneficiary !== BeneficiaryType.NAMED_BENEFICIARY &&
      selectedBeneficiary !== BeneficiaryType.DEATH_ESTATE &&
      (isFeatureEnabled(Feature.FEATURE_REQUIRE_MARITAL_RIGHTS_FOR_SPOUSE) ||
        selectedBeneficiary !== BeneficiaryType.SPOUSE)
        ? maritalRightsExclusion
        : undefined,
    namedBeneficiaries:
      selectedBeneficiary === BeneficiaryType.NAMED_BENEFICIARY
        ? namedBeneficiaries
        : [],
    consentForDocuments,
    languageConfirmation: languageConfirmed,
  };
};

export const getTestGroup = (dateOfBirth: string): string => {
  let testGroup;
  if (Number(dateOfBirth.substring(3, 4)) % 2 === 0) {
    testGroup = TestGroup.TEST_GROUP_A;
  } else testGroup = TestGroup.TEST_GROUP_B;
  Cookies.set('ab-test-group', testGroup);

  return testGroup;
};
